import styled from 'styled-components/native';
import {TouchableOpacity, TouchableOpacityProps} from 'react-native';
import {DefaultTheme} from 'styled-components/native';
import {RFValue} from 'react-native-responsive-fontsize';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'light';
export type ButtonSize = 'small' | 'normal' | 'large';

type Colors = DefaultTheme['colors'];

type SizeConfig = {
  height: number;
  paddingHorizontal: number;
  fontSize: number;
};

// To add a new size, add an entry here — no other file needs to change.
export const BUTTON_SIZES: Record<ButtonSize, SizeConfig> = {
  small: {
    height: 28,
    paddingHorizontal: 16,
    fontSize: 10,
  },
  normal: {
    height: 35,
    paddingHorizontal: 24,
    fontSize: 11,
  },
  large: {
    height: 45,
    paddingHorizontal: 32,
    fontSize: 13,
  },
};

// RFValue reads Dimensions.get('window') on every call, which shrinks while the
// keyboard is open (window resize) and would otherwise make the button resize
// mid-render. Resolve it once here, at module load, so the button keeps a
// stable size regardless of keyboard state.
const RESOLVED_BUTTON_SIZES = Object.fromEntries(
  Object.entries(BUTTON_SIZES).map(([key, config]) => [
    key,
    {
      height: RFValue(config.height),
      paddingHorizontal: config.paddingHorizontal,
      fontSize: RFValue(config.fontSize),
    },
  ]),
) as Record<ButtonSize, SizeConfig>;

type VariantConfig = {
  background: (colors: Colors, customColor?: string) => string;
  textColor: (colors: Colors, customColor?: string) => string;
  borderWidth: number;
  borderColor: (colors: Colors, customColor?: string) => string;
  indicatorColor: (colors: Colors) => string;
};

// To add a new variant, add an entry here — no other file needs to change.
export const BUTTON_VARIANTS: Record<ButtonVariant, VariantConfig> = {
  primary: {
    background: (colors, customColor) => customColor ?? colors.text_dark,
    textColor: (colors, customColor) => (customColor ? colors.shape : colors.buttonTitle),
    borderWidth: 0,
    borderColor: () => 'transparent',
    indicatorColor: colors => colors.background,
  },
  secondary: {
    background: colors => colors.button_secondary,
    textColor: colors => colors.title,
    borderWidth: 0,
    borderColor: () => 'transparent',
    indicatorColor: colors => colors.title,
  },
  outline: {
    background: () => 'transparent',
    textColor: (colors, customColor) => customColor ?? colors.title,
    borderWidth: 2,
    borderColor: (colors, customColor) => customColor ?? colors.text_dark,
    indicatorColor: colors => colors.title,
  },
  light: {
    background: () => 'transparent',
    textColor: colors => colors.title,
    borderWidth: 0,
    borderColor: () => 'transparent',
    indicatorColor: colors => colors.title,
  },
};

interface ContainerProps extends TouchableOpacityProps {
  variant: ButtonVariant;
  size: ButtonSize;
  customColor?: string;
}

interface TitleProps {
  variant: ButtonVariant;
  size: ButtonSize;
  customColor?: string;
}

// @ts-ignore
export const Container = styled(TouchableOpacity)<ContainerProps>`
  height: ${({size}) => RESOLVED_BUTTON_SIZES[size].height}px;
  padding: 0 ${({size}) => RESOLVED_BUTTON_SIZES[size].paddingHorizontal}px;
  width: 100%;
  justify-content: center;
  align-items: center;
  border-radius: ${RFValue(24)}px;
  border-style: solid;
  background-color: ${({variant, customColor, theme}) =>
    BUTTON_VARIANTS[variant].background(theme.colors, customColor)};
  border-width: ${({variant}) => BUTTON_VARIANTS[variant].borderWidth}px;
  border-color: ${({variant, customColor, theme}) =>
    BUTTON_VARIANTS[variant].borderColor(theme.colors, customColor)};
`;

export const Title = styled.Text<TitleProps>`
  font-size: ${({size}) => RESOLVED_BUTTON_SIZES[size].fontSize}px;
  font-family: ${({theme}) => theme.fonts.semibold};
  color: ${({variant, customColor, theme}) =>
    BUTTON_VARIANTS[variant].textColor(theme.colors, customColor)};
`;
